<template>
	<div class="table-wrapper">
		<HeadingSVG imgName="buy-insurance" altText="Buy Insurance" />
		<div class="plan-comparison-table">
			<div class="header cell" v-for="(plan, index) in plans" :key="`header-${plan.id}`">
				<FlipButton :action="() => select(index)" :active="index === activeIndex">
					{{ plan.title }}
				</FlipButton>
			</div>
			<fragment v-for="coverage in coverages" :key="`row-${coverage.id}`">
				<div class="label cell">
					{{ coverage.title }}
					<span class="explanation-trigger">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 17" v-bind:class="'icon'" v-bind:svg-inline="''" @click="explain(coverage)" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M8.5 4.608a.751.751 0 00-.746.741.733.733 0 00.22.537.722.722 0 00.526.214.706.706 0 00.526-.218.724.724 0 00.22-.532.7.7 0 00-.22-.526.724.724 0 00-.526-.216zM7.925 7.418h1.149v4.975H7.925z"/><path d="M8.5 0A8.5 8.5 0 1017 8.5 8.51 8.51 0 008.5 0zm0 16A7.5 7.5 0 1116 8.5 7.508 7.508 0 018.5 16z"/></svg>
					</span>
				</div>
				<div v-for="(plan, index) in plans" :key="plan.id"
					:class="['detail', 'cell', { active: index === activeIndex }]">
					<div class="key" v-if="plan.coverages[coverage.id].deductible">Deductible</div>
					<div class="value" v-if="plan.coverages[coverage.id].deductible">
						{{ plan.coverages[coverage.id].deductible | currency }}
					</div>
					<div class="key">Limit</div>
					<div class="value">{{ plan.coverages[coverage.id].limit | currency }}</div>
					<fragment v-for="(value, label) in plan.coverages[coverage.id].sublimits" :key="label">
						<div class="key">{{ label }}</div>
						<div class="value">{{ value | currency }}</div>
					</fragment>
				</div>
			</fragment>
			<fragment v-if="showCoveragePercent">
				<div class="label cell">
					Coverage Percent
					<span class="explanation-trigger">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 17" v-bind:class="'icon'" v-bind:svg-inline="''" @click="explain({ title: 'Coverage Percent', description: plans[0].coverage_description })" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M8.5 4.608a.751.751 0 00-.746.741.733.733 0 00.22.537.722.722 0 00.526.214.706.706 0 00.526-.218.724.724 0 00.22-.532.7.7 0 00-.22-.526.724.724 0 00-.526-.216zM7.925 7.418h1.149v4.975H7.925z"/><path d="M8.5 0A8.5 8.5 0 1017 8.5 8.51 8.51 0 008.5 0zm0 16A7.5 7.5 0 1116 8.5 7.508 7.508 0 018.5 16z"/></svg>
					</span>
				</div>
				<div v-for="(plan, index) in plans" :key="`coverage-${plan.id}`"
					:class="['detail', 'cell', { active: index === activeIndex }]">
					{{ plan.coverage_percent | percent }}
				</div>
			</fragment>
			<div class="label cell">
				Premium
				<span class="explanation-trigger">
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 17" v-bind:class="'icon'" v-bind:svg-inline="''" @click="explain({ title: 'Premium', description: plans[0].premium_description })" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M8.5 4.608a.751.751 0 00-.746.741.733.733 0 00.22.537.722.722 0 00.526.214.706.706 0 00.526-.218.724.724 0 00.22-.532.7.7 0 00-.22-.526.724.724 0 00-.526-.216zM7.925 7.418h1.149v4.975H7.925z"/><path d="M8.5 0A8.5 8.5 0 1017 8.5 8.51 8.51 0 008.5 0zm0 16A7.5 7.5 0 1116 8.5 7.508 7.508 0 018.5 16z"/></svg>
				</span>
			</div>
			<div v-for="(plan, index) in plans" :key="`coverage-${plan.id}`"
					:class="['premium', 'detail', 'cell', { active: index === activeIndex }]">
				{{ plan.premium | currency }}
			</div>
		</div>
		<MarqueeButton :action="advance">{{ ui.continue_button }}</MarqueeButton>
		<router-link
			:to="`/game/shop/plans/${$store.state.game.insurance.viewing}`"
			class="simple-link"
			@click.native="soundEffect"
		>
			{{ ui.detail_view_link }}
		</router-link>
	</div>
</template>

<script>
import FlipButton from '@/components/base/FlipButton';
import HeadingSVG from '@/components/base/HeadingSVG';
import MarqueeButton from '@/components/base/MarqueeButton';
import { mapActions, mapGetters, mapState } from "vuex";

export default {
	name: 'PlanComparison',
	components: {
		FlipButton,
		HeadingSVG,
		MarqueeButton,
	},
	data: () => ({
		activeIndex: 0,
	}),
	computed: {
		...mapGetters({
			plans: "game/plans/available",
		}),
		...mapState({
			ui: (state) => state.global.api.data.ui.plans,
		}),
		showCoveragePercent() {
			return this.plans.some((plan) => plan.coverage_percent < 1);
		},
		coverages() {
			return Object.values(this.plans[0].coverages).filter((coverage) => !coverage.isSublimt);
		},
	},
	methods: {
		...mapActions({
			activateModal: "global/modal/activate",
		}),
		async select(index) {
			if (index !== this.activeIndex) {
				this.$store.dispatch("sound/fx/play", { id: "select_plan" });
			}
			this.activeIndex = index;
			await this.$store.dispatch("game/plans/select", this.plans[this.activeIndex]);
		},
		advance() {
			this.$store.dispatch("global/analytics/trackEvent", { ec: "ComparePlansView", el: this.$store.state.game.rounds.index, ea: 'Click' });
			this.$router.push(`/game/shop/plans/addons`);
			this.$store.dispatch("sound/fx/play", { id: "button_generic" });
		},
		explain(coverage) {
			this.activateModal({
				component: "ExplanationModal",
				data: {
					heading: coverage.title,
					content: coverage.description,
				},
			});
		},
		soundEffect() {
			this.$store.dispatch("sound/fx/play", { id: "whoosh_generic" });
		},
	},
};
</script>

<style lang="scss" scoped>
	.plan-comparison-table {
		min-width: $max-content-width;
		display: grid;

		grid-template-columns: repeat(5, 20%);
		grid-template-rows: auto;
		grid-gap: 2px;

		margin-bottom: 3em;
		margin-left: -($max-content-width / 5);

		.cell {
			padding: 1.5em .5em;

			&:first-child {
				grid-column-start: 2;
			}

			&.header ::v-deep .two-tone-plan-selector {
				width: 100%;
				margin: 0;

				&.active { color: #ffffff; }

				.side {
					padding-left: 0;
					padding-right: 0;
					font-size: $font-size-lg;
					margin: 0;
				}
			}

			&.label {
				text-align: right;
				word-break: break-word;
			}

			&.detail {
				background: $alt-color-dark;
				text-align: center;
				font-size: $font-size-sm;

				.key { color: $primary-color-light; }
				.value + .key { margin-top: .5em; }

				&.active {
					background: $primary-color-light;
					color: $alt-color-dark;
					.key { color: $alt-color-light; }
				}
			}

			&.premium {
				font-size: $font-size-lg;
				color: $primary-color-light;
			}
		}
	}

	.explanation-trigger  {
		display: block;
		text-align: right;

		.icon {
			display: inline-block;
			width: 1em;
			fill: $primary-color-light;
			cursor: pointer;
		}
	}


</style>
